/**
 * LLM Providers slice for the application store.
 * Handles AI model configuration and selection.
 */

import { llmProviders } from '@/services/api';
import { logger } from '@/utils/logger';

import type { LLMProvider } from '../types';
import type { StateCreator } from 'zustand';

/**
 * LLM Providers state and actions.
 */
export interface LLMProvidersSlice {
  // State
  llmProviders: LLMProvider[];
  isLLMProvidersLoaded: boolean;
  llmProvidersError: string | null;

  // Actions
  setLLMProviders: (providers: LLMProvider[]) => void;
  setIsLLMProvidersLoaded: (loaded: boolean) => void;
  setLLMProvidersError: (error: string | null) => void;
  loadLLMProviders: () => Promise<void>;
}

/**
 * Creates the LLM providers slice.
 */
export const createLLMProvidersSlice: StateCreator<LLMProvidersSlice, [], [], LLMProvidersSlice> = (
  set,
  get
) => ({
  // Initial state
  llmProviders: [],
  isLLMProvidersLoaded: false,
  llmProvidersError: null,

  // Actions
  setLLMProviders: providers => set({ llmProviders: providers }),

  setIsLLMProvidersLoaded: loaded => set({ isLLMProvidersLoaded: loaded }),

  setLLMProvidersError: error => set({ llmProvidersError: error }),

  loadLLMProviders: async () => {
    const state = get();

    // Don't reload if already loaded
    if (state.isLLMProvidersLoaded) {
      return;
    }

    try {
      set({ llmProvidersError: null });
      const providers = await llmProviders.getAll();
      set({
        llmProviders: providers,
        isLLMProvidersLoaded: true,
      });
    } catch (error) {
      logger.error('Failed to load LLM providers:', error);
      set({
        llmProvidersError: 'Failed to load AI models',
        isLLMProvidersLoaded: false,
      });
    }
  },
});
